/**
 * PATH: packages/pricelist/src/xlsx.ts
 * FILE_ID: PKG-PRICELIST-XLSX-001
 * PURPOSE: Pure-ish I→O helpers to export sections→workbook and import a
 *          workbook→rows, so a whole pricelist round-trips through Excel.
 * WHY: Tradies live in Excel. One section = one sheet; the header row is the
 *      column labels (core + schema), each data row is an item.
 * RELATED: columns.ts (CORE_FIELDS), types.ts, PricelistWorkbook
 */
import * as XLSX from 'xlsx'
import { CORE_FIELDS } from './columns'
import type { GridRow } from './columns'
import type { ColumnDef } from './types'

export interface SheetSpec {
  name: string
  schema: ColumnDef[]
  rows: GridRow[]
}

const CORE_HEADER: Record<string, string> = {
  item_code: 'Code',
  item_name: 'Description',
  unit: 'Unit',
  base_cost: 'Cost',
  markup_percent: 'Markup %',
  computed_sell_price: 'Sell',
}

/** sections → XLSX ArrayBuffer (one sheet per section). */
export function sectionsToWorkbook(sheets: SheetSpec[]): ArrayBuffer {
  const wb = XLSX.utils.book_new()
  for (const sheet of sheets) {
    const headerKeys = [...CORE_FIELDS, ...sheet.schema.map((c) => c.key)]
    const headerRow = [
      ...CORE_FIELDS.map((f) => CORE_HEADER[f]),
      ...sheet.schema.map((c) => c.label || c.key),
    ]
    const body = sheet.rows.map((r) => headerKeys.map((k) => r[k] ?? ''))
    const ws = XLSX.utils.aoa_to_sheet([headerRow, ...body])
    const safeName = (sheet.name || 'Section').slice(0, 31).replace(/[\\/?*[\]:]/g, '-')
    XLSX.utils.book_append_sheet(wb, ws, safeName || 'Section')
  }
  return XLSX.write(wb, { bookType: 'xlsx', type: 'array' }) as ArrayBuffer
}

/** Trigger a browser download of the workbook. */
export function downloadWorkbook(sheets: SheetSpec[], filename: string): void {
  const buf = sectionsToWorkbook(sheets)
  const blob = new Blob([buf], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`
  a.click()
  URL.revokeObjectURL(url)
}

export interface ParsedSheet {
  name: string
  rows: Array<Record<string, unknown>>
  headers: string[]
}

/**
 * Parse a workbook ArrayBuffer → {name, headers, rows{label→value}} per sheet.
 * The caller maps labels back to column keys against the live schema.
 */
export function parseWorkbook(buf: ArrayBuffer): ParsedSheet[] {
  const wb = XLSX.read(buf, { type: 'array' })
  const out: ParsedSheet[] = []
  for (const name of wb.SheetNames) {
    const ws = wb.Sheets[name]
    const aoa = XLSX.utils.sheet_to_json<unknown[]>(ws, { header: 1, blankrows: false })
    if (!aoa.length) {
      out.push({ name, headers: [], rows: [] })
      continue
    }
    const headers = (aoa[0] as unknown[]).map((h) => String(h ?? '').trim())
    const rows = aoa.slice(1).map((line) => {
      const rec: Record<string, unknown> = {}
      headers.forEach((h, i) => {
        rec[h] = (line as unknown[])[i]
      })
      return rec
    })
    out.push({ name, headers, rows })
  }
  return out
}

/** Map a parsed (label→value) row to a grid row using a label→key index. */
export function mapParsedRow(
  rec: Record<string, unknown>,
  labelToKey: Record<string, string>,
): Record<string, unknown> {
  const row: Record<string, unknown> = {}
  for (const [label, value] of Object.entries(rec)) {
    const key = labelToKey[label] ?? labelToKey[label.trim()]
    if (key) row[key] = value
  }
  return row
}
